import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import ProgramSummary from '../components/ProgramSummary';
import OptionCard from '../components/OptionCard';

const OptionsSelection = () => {
  const navigate = useNavigate();
  const [programData, setProgramData] = useState(null);
  const [currentStep] = useState(2);
  const [options, setOptions] = useState({
    coup_envoi: false,
    diagnostic_individuel: 0,
    esprit_equipe: false,
    sortir_du_bois: false,
    panorama_flash: 0
  });
  const [maxDiagnostics, setMaxDiagnostics] = useState(0);
  const [maxPanorama, setMaxPanorama] = useState(0);

  useEffect(() => {
    // Charger le programme depuis localStorage
    const savedProgram = localStorage.getItem('simulator_program');
    if (!savedProgram) {
      navigate('/');
      return;
    }

    const program = JSON.parse(savedProgram);
    setProgramData(program);

    // Calculer les maximums pour les options
    let totalLearners = 0;
    const uniqueModules = new Set();
    
    program.groups.forEach(group => {
      group.learners.forEach(learner => {
        totalLearners++;
        learner.modules.forEach(module => {
          uniqueModules.add(module.module_id);
        });
      });
    });

    setMaxDiagnostics(totalLearners);
    setMaxPanorama(uniqueModules.size);
    
    // Charger les options existantes si elles existent
    const savedOptions = localStorage.getItem('simulator_options');
    if (savedOptions) {
      try {
        const optionsData = JSON.parse(savedOptions);
        setOptions(optionsData);
      } catch (error) {
        console.error('Erreur lors du chargement des options:', error);
      }
    }

    // Auto-scroll vers les cartes d'options après un petit délai
    setTimeout(() => {
      const optionsSection = document.querySelector('.options-grid');
      if (optionsSection) {
        optionsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  }, [navigate]);

  const optionsConfig = [
    {
      id: 'coup_envoi',
      title: 'Coup d\'envoi',
      details: 'Quand un programme est déployé sur un groupe, il est important de créer un narratif commun qui fait sens. Cette option permet de lancer un programme collectif dans les meilleures conditions',
      price: 900,
      type: 'checkbox',
      selectionText: 'Sélectionner'
    },
    {
      id: 'diagnostic_individuel',
      title: 'Diagnostic individuel',
      details: 'Pas toujours facile de savoir quelle compétence apporter. Améliorez la sélection des modules par une évaluation poussée et impliquez l\'apprenant dans la construction de son programme.',
      price: 900,
      priceUnit: ' /apprenant',
      type: 'counter',
      max: maxDiagnostics
    },
    {
      id: 'esprit_equipe',
      title: 'Esprit d\'équipe',
      details: 'Profitez de cette dynamique d\'apprentissage collective pour améliorer l\'entreprise au travers d\'un projet en équipe. Cette "œuvre" commune renforcera en prime le sentiment d\'appartenance.',
      price: 3500,
      type: 'checkbox',
      selectionText: 'Sélectionner'
    },
    {
      id: 'sortir_du_bois',
      title: 'Sortir du bois',
      details: 'La participation de la direction de "l\'œuvre" commune est un acte fort. Sa présence renforce l\'impact du projet, son exemplarité développe la confiance des équipes.',
      price: 1500,
      type: 'checkbox',
      selectionText: 'Sélectionner',
      dependency: 'esprit_equipe',
      dependencyText: 'Uniquement disponible avec l\'option "Esprit d\'équipe"'
    },
    {
      id: 'panorama_flash',
      title: 'Panorama flash',
      details: 'La formation étant une délégation de management, profitez de cette option pour développer un langage commun avec le manager formé. Cette option renforce en plus la considération des efforts d\'apprentissage.',
      price: 250,
      priceUnit: ' /module',
      type: 'counter',
      max: maxPanorama
    }
  ];

  const handleOptionChange = (optionId, value) => {
    setOptions(prev => ({ ...prev, [optionId]: value }));
  };

  const validateOptions = () => {
    // Sauvegarder les options dans localStorage
    localStorage.setItem('simulator_options', JSON.stringify(options));
    navigate('/contact');
  };

  if (!programData) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Titre principal */}
      <div className="bg-[#fff5ee] py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-center mb-8">Construisez votre programme de formation</h1>
          <Breadcrumb currentStep={currentStep} />
        </div>
      </div>
      
      {/* Récapitulatif du programme */}
      <div className="summary-container">
        <div className="max-w-6xl mx-auto px-4">
          <ProgramSummary 
            programData={programData} 
            showEditButton={true}
            editButtonText="Modifier le programme"
            editRoute="/"
          />
        </div>
      </div>

      {/* Grille des options */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Texte explicatif au-dessus des options */}
        <div style={{ 
          marginBottom: '30px', 
          fontSize: '14px', 
          color: '#666666',
          lineHeight: '1.6',
          textAlign: 'left'
        }}>
          Sélectionnez les options pour impliquer la direction et les formés dans le programme de formation. Pour le diagnostic individuel et le panorama flash, vous préciserez les personnes et les modules concernés après la réception du devis.
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 options-grid">
          {optionsConfig.map(option => (
            <OptionCard
              key={option.id}
              option={option}
              value={options[option.id]}
              onChange={(value) => handleOptionChange(option.id, value)}
              disabled={option.dependency && !options[option.dependency]}
            />
          ))}
        </div>

        {/* Bouton validation */}
        <div className="text-center mt-12">
          <button
            onClick={validateOptions}
            className="btn-primary"
            data-testid="validate-options-button"
          >
            Valider les options
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptionsSelection;